import { DeleteOutlined } from '@ant-design/icons'
import { User } from '@components/user/User'
import { UserLegal } from '@models/User'
import { fetchUserLegal } from '@services/UserService'
import { useAppSelector } from '@store/hooks'
import { selectSelectedUser } from '@store/UserReducer'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

const { Text, Paragraph } = Typography
const LegalsTab = () => {
  const user = useAppSelector(selectSelectedUser())
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [userLegal, setUserLegal] = useState<UserLegal | undefined>(undefined)

  const delay: number = useMemo(
    () =>
      !userLegal
        ? 0
        : dayjs(userLegal.lastInteraction.date).diff(
            userLegal.userRetentionPeriod,
            'days'
          ),
    [userLegal]
  )

  useEffect(() => {
    if (!user) return
    setIsLoading(true)
    fetchUserLegal(user.id)
      .then((data) => setUserLegal(data))
      .finally(() => setIsLoading(false))
  }, [user])

  if (!user) {
    return <Spin />
  }

  return (
    <Row gutter={[32, 32]}>
      <Col sm={12} xs={24}>
        <Row gutter={[32, 32]}>
          <Col span={24}>
            <Space orientation="vertical" className="w-full" size={10}>
              <PageTitle
                level={5}
                title="User data retention"
                style={{ margin: 0 }}
              />
              <Card loading={isLoading || !userLegal}>
                <Space orientation="vertical" size={15}>
                  <Flex gap={40}>
                    <Space orientation="vertical" size={5}>
                      <Text className="m-0" strong>
                        Last interaction date
                      </Text>
                      <Text>
                        {userLegal !== undefined
                          ? dayjs(userLegal.lastInteraction.date).format(
                              'DD/MM/YYYY'
                            )
                          : '--'}
                      </Text>
                    </Space>
                    <Space orientation="vertical" size={5}>
                      <Text className="m-0" strong>
                        Recommended deletion date
                      </Text>
                      <Text
                        style={{
                          color: !userLegal?.lastInteraction
                            ? undefined
                            : delay <= 0
                              ? 'red'
                              : delay <= 30
                                ? 'orange'
                                : undefined,
                        }}
                      >
                        {userLegal?.lastInteraction
                          ? dayjs(userLegal.lastInteraction.date)
                              .add(userLegal.userRetentionPeriod ?? 0, 'days')
                              .format('DD/MM/YYYY')
                          : '--'}
                      </Text>
                    </Space>
                  </Flex>

                  <Paragraph type="secondary" className="m-0">
                    This interaction refers to the last reservation's date made
                    via Pléthore or the last event participation. In case the
                    user did not already book, the registration date is
                    displayed.
                    <br />
                    This user record is kept for{' '}
                    <b>up to 3 years after the last interaction,</b> in
                    accordance with data protection regulations. After this
                    period, <b>it should be deleted or anonymized.</b> You can
                    update or delete it earlier if the user requests it.
                  </Paragraph>
                </Space>
              </Card>
            </Space>
          </Col>
          <Col span={24}>
            <Space orientation="vertical" className="w-full" size={10}>
              <PageTitle
                level={5}
                title={'Delete user profile'}
                style={{ margin: 0 }}
              />
              <Alert
                description={
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Text>
                        Deleting the user will also
                        <b> delete all associated resources </b>- i.e.
                        reservations, membership profiles, providers profiles,
                        account...
                        <br />
                        <b>This action is immediate and cannot be undone. </b>
                        If logged in the app,
                        <b> the user will be logged out</b>.
                      </Text>
                    </Col>
                    <Col span={24}>
                      <SBRM.Actions
                        entity={Entity.user}
                        entityId={user.id}
                        actions={[SBRMAction.Delete]}
                        trigger={
                          <Button
                            type="primary"
                            danger
                            block
                            icon={<DeleteOutlined />}
                          >
                            Delete
                          </Button>
                        }
                      />
                    </Col>
                  </Row>
                }
                type="error"
              />
            </Space>
          </Col>
        </Row>
      </Col>
      <Col sm={12} xs={24}>
        <Row gutter={[32, 32]}>
          <Col span={24}>
            <Space orientation="vertical" className="w-full" size={10}>
              <PageTitle
                level={5}
                title="Marketing Opt-in"
                style={{ margin: 0 }}
              />
              <Card loading={isLoading || !userLegal}>
                <Text>
                  {userLegal?.marketingOptInAt
                    ? `Opt-in date: ${dayjs(userLegal?.marketingOptInAt).format('DD/MM/YYYY')}`
                    : 'User has not opted in to marketing.'}
                </Text>
              </Card>
            </Space>
          </Col>
          <Col span={24}>
            <User.OneSignal userId={user.id} />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default LegalsTab
